import { useEffect, useRef, useState, cloneElement } from "react";
import axios from "axios";
import { Heart, Share2, Link as LinkIcon, Check } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public "Listener Top 10" wall — the most-hearted tracks across all
 * listeners. Each row links to a Spotify and YouTube search so visitors
 * can play any track they spot in one click, and a share menu so they
 * can boast about hyping their favorite track up the wall.
 *
 * URL deeplink: a query param `?track=<slug>` will scroll the page to that
 * row and pulse it red on load. Share links generated by this component
 * use that same scheme.
 *
 * Polls every 60s. Hides itself entirely when no tracks have been hearted
 * yet, so we never show a blank wall on day one.
 */
export default function TopTracks() {
  const [tracks, setTracks] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [voter, setVoter] = useState(null);
  const [highlightSlug, setHighlightSlug] = useState(null);
  const rowRefs = useRef({}); // slug -> dom node

  useEffect(() => {
    let cancelled = false;
    const fetchTop = async () => {
      try {
        const [topRes, voterRes] = await Promise.all([
          axios.get(`${API}/top-tracks`, { params: { limit: 10 } }),
          axios.get(`${API}/voter-of-the-week`),
        ]);
        if (cancelled) return;
        setTracks(Array.isArray(topRes.data) ? topRes.data : []);
        setVoter(voterRes?.data?.ok ? voterRes.data : null);
      } catch (_) {
        /* silent */
      } finally {
        if (!cancelled) setLoaded(true);
      }
    };
    fetchTop();
    const id = setInterval(fetchTop, 60000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  // Read ?track=<slug> from the URL exactly once on mount. We stash the
  // slug; once the tracks list is loaded and that row's ref is registered,
  // we scroll to it and pulse it red.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const slug = params.get("track");
      if (slug) setHighlightSlug(slug);
    } catch (e) {
      /* no-op */
    }
  }, []);

  useEffect(() => {
    if (!highlightSlug || tracks.length === 0) return;
    const node = rowRefs.current[highlightSlug];
    if (!node) return;
    // Smooth scroll into view, then auto-clear the highlight after the pulse.
    node.scrollIntoView({ behavior: "smooth", block: "center" });
    const t = setTimeout(() => setHighlightSlug(null), 4000);
    return () => clearTimeout(t);
  }, [highlightSlug, tracks]);

  // Anchor-scroll rescue: when a visitor lands on `/#listener-top-10` from a
  // Facebook / X share card, the browser tries to scroll to the anchor
  // *before* the tracks API returns — so the section isn't in the DOM yet
  // and the auto-scroll silently fails. Retry the scroll once the section
  // has actually rendered. Also runs on hashchange for good measure.
  useEffect(() => {
    if (!loaded) return;
    if (typeof window === "undefined") return;
    if (window.location.hash !== "#listener-top-10") return;
    const section = document.getElementById("listener-top-10");
    if (!section) return;
    // Small delay to let the persistent player bar / fixed header settle
    // so the scroll target lines up correctly.
    const t = setTimeout(() => {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 250);
    return () => clearTimeout(t);
  }, [loaded]);

  if (loaded && tracks.length === 0) return null;

  return (
    <section
      id="listener-top-10"
      className="relative border-t border-zinc-800 bg-zinc-950 py-16 lg:py-24 scroll-mt-24"
      data-testid="listener-top-tracks"
    >
      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        <div className="flex items-end justify-between gap-4 mb-8 lg:mb-10">
          <div>
            <div className="text-[10px] tracking-[0.4em] text-red-500 uppercase font-display mb-2 flex items-center gap-2">
              <Heart className="w-3.5 h-3.5" fill="currentColor" strokeWidth={0} />
              //_listener_top_10
            </div>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-zinc-50 leading-none">
              MOST <span className="text-red-500">HEARTED</span>
            </h2>
            <p className="text-sm text-zinc-400 mt-2 max-w-md">
              Voted by you — heart a track in The Pit and watch it climb the wall.
            </p>
          </div>
        </div>

        <ol className="divide-y divide-zinc-800 border-y border-zinc-800">
          {tracks.map((t, idx) => {
            const rank = idx + 1;
            const slug = slugifyTrack(t.artist, t.title);
            const query = encodeURIComponent(
              t.artist ? `${t.artist} ${t.title}` : t.title
            );
            const spotifyUrl = `https://open.spotify.com/search/${query}`;
            const youtubeUrl = `https://www.youtube.com/results?search_query=${query}`;
            const isHighlighted = highlightSlug === slug;
            return (
              <li
                key={t.track_key}
                ref={(el) => {
                  if (el) rowRefs.current[slug] = el;
                }}
                className={`group flex items-center gap-4 py-3 lg:py-4 px-2 lg:px-4 transition-colors ${
                  isHighlighted
                    ? "bg-red-600/15 ring-1 ring-red-600 drr-pulse"
                    : "hover:bg-zinc-900/60"
                }`}
                data-testid="listener-top-track-row"
                data-track-slug={slug}
              >
                <span
                  className={`font-display text-3xl lg:text-4xl tracking-tight shrink-0 w-12 lg:w-16 text-right ${
                    rank === 1
                      ? "text-red-500"
                      : rank <= 3
                      ? "text-zinc-200"
                      : "text-zinc-600"
                  }`}
                  data-testid="listener-top-track-rank"
                >
                  {String(rank).padStart(2, "0")}
                </span>
                <div className="flex-1 min-w-0">
                  <div
                    className="font-display text-lg lg:text-xl text-zinc-50 tracking-tight truncate"
                    title={t.title}
                    data-testid="listener-top-track-title"
                  >
                    {t.title}
                  </div>
                  {t.artist && (
                    <div
                      className="text-xs lg:text-sm text-zinc-400 tracking-wide truncate"
                      title={t.artist}
                      data-testid="listener-top-track-artist"
                    >
                      {t.artist}
                    </div>
                  )}
                  <VotedByLine voters={t.voters} hearts={t.hearts} />
                </div>
                <div className="shrink-0 flex items-center gap-2 lg:gap-3">
                  <a
                    href={spotifyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Find ${t.title} on Spotify`}
                    title={`Find "${t.title}" on Spotify`}
                    className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-[#1DB954] hover:bg-zinc-950 border border-zinc-800 hover:border-[#1DB954] transition-colors"
                    data-testid="listener-top-track-spotify"
                  >
                    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden>
                      <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.299.421-1.02.599-1.559.3z"/>
                    </svg>
                  </a>
                  <a
                    href={youtubeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Find ${t.title} on YouTube`}
                    title={`Find "${t.title}" on YouTube`}
                    className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-[#FF0000] hover:bg-zinc-950 border border-zinc-800 hover:border-[#FF0000] transition-colors"
                    data-testid="listener-top-track-youtube"
                  >
                    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden>
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                    </svg>
                  </a>
                  <ShareButton track={t} slug={slug} rank={rank} />
                  <div
                    className="flex items-center gap-1.5 ml-2 lg:ml-4 shrink-0"
                    data-testid="listener-top-track-hearts"
                  >
                    <Heart
                      className="w-4 h-4 text-red-500"
                      fill="currentColor"
                      strokeWidth={0}
                    />
                    <span className="font-display text-lg lg:text-xl text-zinc-100 tabular-nums">
                      {t.hearts}
                    </span>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>

        <VoterOfTheWeek voter={voter} />

        <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-[10px] tracking-[0.3em] text-zinc-600 uppercase font-display">
            //_open_the_pit_chat · heart any track in the recently-played panel to vote
          </p>
          <Top10ShareButton />
        </div>
      </div>
    </section>
  );
}

/**
 * "//_TOP_VOTER" callout that highlights the listener who's hearted the
 * most tracks in the past 7 days. Hidden if no named voter in the window.
 */
function VoterOfTheWeek({ voter }) {
  if (!voter || !voter.nickname) return null;
  const tracksLabel =
    voter.distinct_tracks === 1 ? "1 track" : `${voter.distinct_tracks} different tracks`;
  const heartsLabel = voter.hearts === 1 ? "1 heart" : `${voter.hearts} hearts`;
  return (
    <div
      className="mt-8 border-2 border-red-600 bg-red-600/5 p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4"
      data-testid="voter-of-the-week"
    >
      <div className="flex-1 min-w-0">
        <div className="text-[10px] tracking-[0.4em] text-red-500 uppercase font-display mb-1 flex items-center gap-2">
          <Heart className="w-3 h-3" fill="currentColor" strokeWidth={0} />
          //_voter_of_the_week
        </div>
        <div className="font-display text-2xl md:text-3xl text-zinc-50 tracking-tight leading-none">
          <span data-testid="voter-of-the-week-nickname" className="text-red-500">
            @{voter.nickname}
          </span>
        </div>
        <p className="text-xs md:text-sm text-zinc-300 mt-2 tracking-wide">
          {heartsLabel} dropped across {tracksLabel} in the last 7 days. The
          loudest super-fan in The Pit right now.
        </p>
      </div>
      <div className="shrink-0 grid grid-cols-2 gap-3 md:gap-4 text-center">
        <div className="border border-zinc-800 bg-black/40 px-4 py-2 md:px-5 md:py-3">
          <div className="font-display text-2xl md:text-3xl text-zinc-50 leading-none tabular-nums">
            {voter.hearts}
          </div>
          <div className="text-[9px] tracking-[0.3em] text-zinc-500 uppercase mt-1">
            hearts
          </div>
        </div>
        <div className="border border-zinc-800 bg-black/40 px-4 py-2 md:px-5 md:py-3">
          <div className="font-display text-2xl md:text-3xl text-zinc-50 leading-none tabular-nums">
            {voter.distinct_tracks}
          </div>
          <div className="text-[9px] tracking-[0.3em] text-zinc-500 uppercase mt-1">
            tracks
          </div>
        </div>
      </div>
    </div>
  );
}

function Top10ShareButton() {
  // Include a week-bucket cache buster so Facebook / LinkedIn re-scrape the
  // OG tags at least once per week instead of caching an outdated Top 10
  // snapshot for 30+ days. `v=YYYYWW` changes weekly on Sunday UTC and any
  // fresh URL forces the social platform to re-fetch fresh og:description
  // with the current #1/#2/#3 tracks.
  const weekBucket = (() => {
    const d = new Date();
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const week = Math.ceil(((d - yearStart) / 86400000 + yearStart.getUTCDay() + 1) / 7);
    return `${d.getUTCFullYear()}${String(week).padStart(2, "0")}`;
  })();
  const shareUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/api/share/top10?v=${weekBucket}`
      : "";
  const shareText =
    "🤘 The Digital Revolution Radio Listener Top 10 — voted by metalheads worldwide. Listen + vote at";
  return (
    <SharePopover
      shareUrl={shareUrl}
      shareText={shareText}
      nativeTitle="Digital Revolution Radio — Listener Top 10"
      menuLabel="//_share_the_wall"
      menuTestId="listener-top-10-share-menu"
    >
      <button
        type="button"
        aria-label="Share the Listener Top 10"
        title="Share the Listener Top 10"
        className="inline-flex items-center gap-2 px-4 py-2 border border-red-600 bg-red-600/10 hover:bg-red-600 hover:text-white text-red-500 font-display text-xs tracking-[0.25em] uppercase transition-colors"
        data-testid="listener-top-10-share-btn"
      >
        <Share2 className="w-4 h-4" strokeWidth={2.2} />
        Share The Top 10
      </button>
    </SharePopover>
  );
}

// Build a clean, URL-safe slug like "metallica-master-of-puppets" from
// artist + title. Used as the `?track=` deeplink AND as React row keys
// for scroll-into-view.
function slugifyTrack(artist, title) {
  const raw = `${artist || ""} ${title || ""}`.toLowerCase();
  return (
    raw
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")    // strip accents
      .replace(/&/g, " and ")
      .replace(/[^a-z0-9]+/g, "-")        // non-alnum -> dash
      .replace(/^-+|-+$/g, "")            // trim dashes
      .slice(0, 80) || "track"
  );
}

/**
 * Tiny "Voted by @nick1, @nick2 + N others" line under each track row.
 * Only renders when at least one named voter exists. Anonymous hearts
 * still count toward the total — the "+ N others" rolls them in.
 * Clicking/tapping the line (or the "+N others" link) opens a popover
 * with the full deduped list of named voters, so listeners can see the
 * whole crew that pushed a track into the chart.
 */
function VotedByLine({ voters, hearts }) {
  const [open, setOpen] = useState(false);
  if (!Array.isArray(voters) || voters.length === 0) return null;
  const shown = voters.slice(0, 3);
  const remaining = Math.max(0, (hearts || voters.length) - shown.length);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="text-[10px] text-zinc-500 tracking-wide mt-1 truncate text-left w-full hover:text-zinc-300 focus:outline-none focus-visible:ring-1 focus-visible:ring-red-500 transition-colors"
          data-testid="listener-top-track-voted-by"
          aria-label={`See all ${voters.length} voter${voters.length === 1 ? "" : "s"}`}
        >
          <span className="text-zinc-600 uppercase tracking-[0.25em] mr-1">
            //_voted_by
          </span>
          {shown.map((v, i) => (
            <span key={v + i}>
              <span className="text-red-500/90">@{v}</span>
              {i < shown.length - 1 ? <span className="text-zinc-700">, </span> : null}
            </span>
          ))}
          {remaining > 0 && (
            <span className="text-zinc-400 underline decoration-dotted underline-offset-2">
              {" "}
              + {remaining} other{remaining === 1 ? "" : "s"}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        sideOffset={6}
        className="w-72 bg-zinc-950 border border-red-500/50 text-zinc-200 p-0 rounded-none"
        data-testid="listener-top-track-voted-by-popover"
      >
        <div className="border-b border-zinc-800 px-3 py-2 flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-[0.3em] text-red-500">
            //_voted_by
          </span>
          <span className="text-[10px] text-zinc-500 tracking-wider">
            {voters.length} {voters.length === 1 ? "VOTER" : "VOTERS"}
          </span>
        </div>
        <div className="max-h-60 overflow-y-auto px-3 py-2">
          <ul className="space-y-1">
            {voters.map((v, i) => (
              <li
                key={v + i}
                className="text-xs text-zinc-200 flex items-baseline gap-2"
              >
                <span className="text-[10px] text-zinc-600 font-mono w-5 text-right">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-red-500/90">@</span>
                <span>{v}</span>
              </li>
            ))}
          </ul>
        </div>
        {(hearts || 0) > voters.length && (
          <div className="border-t border-zinc-800 px-3 py-2 text-[10px] text-zinc-500 tracking-wide">
            + {hearts - voters.length} additional heart
            {hearts - voters.length === 1 ? "" : "s"} (anonymous)
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function ShareButton({ track, slug, rank }) {
  const shareUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/api/share/track/${slug}`
      : "";
  const label = track.artist ? `${track.artist} — ${track.title}` : track.title;
  const shareText = `🤘 "${label}" is currently #${rank} on the Digital Revolution Radio Listener Top 10. Listen + vote at`;
  const nativeTitle = `Digital Revolution Radio — ${label}`;

  return (
    <SharePopover
      shareUrl={shareUrl}
      shareText={shareText}
      nativeTitle={nativeTitle}
      menuLabel="//_share_track"
      menuTestId="listener-top-track-share-menu"
    >
      <button
        type="button"
        aria-label={`Share ${label}`}
        title={`Share "${label}"`}
        className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-red-500 hover:bg-zinc-950 border border-zinc-800 hover:border-red-600 transition-colors"
        data-testid="listener-top-track-share"
      >
        <Share2 className="w-4 h-4" strokeWidth={2} />
      </button>
    </SharePopover>
  );
}

/**
 * Generic brutalist share popover. Wraps any trigger element with a
 * "Copy link / X / Facebook" menu and prefers the Web Share API on mobile.
 *
 * Usage:
 *   <SharePopover shareUrl="..." shareText="..." nativeTitle="...">
 *     <button>...</button>
 *   </SharePopover>
 */
function SharePopover({
  shareUrl,
  shareText,
  nativeTitle,
  menuLabel = "//_share",
  menuTestId,
  children,
}) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
    shareText
  )}&url=${encodeURIComponent(shareUrl)}`;
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
    shareUrl
  )}&quote=${encodeURIComponent(shareText)}`;

  const handleNativeShare = async () => {
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title: nativeTitle, text: shareText, url: shareUrl });
        setOpen(false);
        return true;
      } catch (e) {
        return false;
      }
    }
    return false;
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error("Could not copy link");
    }
  };

  const handleTriggerClick = async (e) => {
    // On mobile (Web Share API available) we prefer the native sheet — it's
    // strictly better than our popover because it includes the user's real
    // app list. If the user dismisses or the API isn't available, the
    // popover opens via the Trigger.
    if (typeof navigator !== "undefined" && navigator.share) {
      e.preventDefault();
      const ok = await handleNativeShare();
      if (!ok) setOpen(true);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {/* Clone the user-provided trigger and inject our click handler so
            the Web Share API path works on mobile without breaking the
            popover fallback on desktop. */}
        {(() => {
          const child = Array.isArray(children) ? children[0] : children;
          if (!child) return null;
          const userOnClick = child.props?.onClick;
          return cloneElement(child, {
            onClick: async (e) => {
              if (userOnClick) userOnClick(e);
              await handleTriggerClick(e);
            },
          });
        })()}
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="end"
        sideOffset={8}
        className="w-56 p-0 bg-zinc-950 border border-zinc-700 rounded-none shadow-[0_0_0_1px_rgba(239,68,68,0.25)]"
        data-testid={menuTestId}
      >
        <div className="px-3 py-2 border-b border-zinc-800 text-[10px] tracking-[0.3em] uppercase text-red-500 font-display">
          {menuLabel}
        </div>
        <div className="p-1 flex flex-col">
          <button
            type="button"
            onClick={handleCopy}
            className="flex items-center gap-3 px-3 py-2 text-sm text-zinc-200 hover:bg-zinc-900 hover:text-white transition-colors text-left"
            data-testid="share-copy-link"
          >
            {copied ? (
              <Check className="w-4 h-4 text-red-500 shrink-0" />
            ) : (
              <LinkIcon className="w-4 h-4 text-zinc-400 shrink-0" />
            )}
            <span className="flex-1 tracking-wide">{copied ? "Copied!" : "Copy link"}</span>
          </button>
          <a
            href={twitterUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 px-3 py-2 text-sm text-zinc-200 hover:bg-zinc-900 hover:text-white transition-colors"
            data-testid="share-twitter"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4 text-zinc-400 shrink-0" fill="currentColor" aria-hidden>
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
            <span className="flex-1 tracking-wide">Share to X</span>
          </a>
          <a
            href={facebookUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 px-3 py-2 text-sm text-zinc-200 hover:bg-zinc-900 hover:text-white transition-colors"
            data-testid="share-facebook"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4 text-zinc-400 shrink-0" fill="currentColor" aria-hidden>
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
            <span className="flex-1 tracking-wide">Share to Facebook</span>
          </a>
        </div>
      </PopoverContent>
    </Popover>
  );
}
